import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { AssessmentPageInterface } from 'app/assessment/models';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { Recommendation, RecommendationTemplateInterface, RecommendationTemplateRuleInterface } from 'app/recommendation/models';
import { RecommendationInterface } from 'app/recommendation/models/recommendation.model';
import { RecommendationRestConnectorService } from 'app/recommendation/services/recommendation-rest-connector.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { RecommendationCategoryInterface } from '../models/recommendation-category.model';
import { RecommendationServiceFilters } from '../services/recommendation.service';

import {
  CreateRecommendationCategory,
  CreateRecommendationCategoryCompleted,
  CreateRecommendationTemplate,
  CreateRecommendationTemplateCompleted,
  CreateRecommendationTemplateRule,
  CreateRecommendationTemplateRuleCompleted,
  DeleteEmployeeRecommendation,
  DeleteEmployeeRecommendationCompleted,
  DeleteRecommendationCategory,
  DeleteRecommendationCategoryCompleted,
  DeleteRecommendationTemplate,
  DeleteRecommendationTemplateCompleted,
  DeleteRecommendationTemplateRule,
  DeleteRecommendationTemplateRuleCompleted,
  LoadAssessmentQuestionsCompleted,
  LoadEmployeeRecommendations,
  LoadEmployeeRecommendationsCompleted,
  LoadRecommendationsLibrary,
  LoadRecommendationsLibraryCompleted,
  RecommendationActionType,
  UpdateEmployeeRecommendation,
  UpdateEmployeeRecommendationCompleted,
  UpdateRecommendationCategory,
  UpdateRecommendationCategoryCompleted,
  UpdateRecommendationTemplate,
  UpdateRecommendationTemplateCompleted,
  UpdateRecommendationTemplateRule,
  UpdateRecommendationTemplateRuleCompleted,
} from './actions';


@Injectable()
export class RecommendationEffects {
  public source: string = 'RecommendationEffects';

  /**
   * Load all available entities.
   */
  @Effect()
  public loadEmployeeRecommendations$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.LoadEmployeeRecommendations),
      map((action: LoadEmployeeRecommendations) => action.payload),
      switchMap((recommendationFilters: RecommendationServiceFilters) => {
        return this.recommendationRestConnector.loadAll(recommendationFilters).pipe(
          map((recommendations: Recommendation[]) => new LoadEmployeeRecommendationsCompleted(recommendations)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  /**
 * Load recommendation templates library.
 */
  @Effect()
  public loadRecommendationTemplatesLibrary$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.LoadRecommendationsLibrary),
      switchMap(() => {
        return this.recommendationRestConnector.loadRecommendationTemplatesLibrary().pipe(
          map((recommendationTemplatesLibrary: RecommendationCategoryInterface[]) =>
            new LoadRecommendationsLibraryCompleted(recommendationTemplatesLibrary)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Load recommendation templates library.
  */
  @Effect()
  public loadAssessmentQuestions$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.LoadAssessmentQuestions),
      switchMap(() => {
        return this.recommendationRestConnector.loadAssessmentQuestions().pipe(
          map((assessmentQuestionData: AssessmentPageInterface[]) =>
            new LoadAssessmentQuestionsCompleted(assessmentQuestionData)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Update the provided entity.
   */
  @Effect()
  public updateEmployeeRecommendation$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.UpdateEmployeeRecommendation),
      map((action: UpdateEmployeeRecommendation) => action.payload),
      switchMap((recommendation: RecommendationInterface) => {
        return this.recommendationRestConnector.updateEmployeeRecommendation(recommendation).pipe(
          map((recommendation: Recommendation) => new UpdateEmployeeRecommendationCompleted(recommendation)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
     );

  /**
   * Delete recommendation based on id
   */
  @Effect()
  public deleteEmployeeRecommendation$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.DeleteEmployeeRecommendation),
      map((action: DeleteEmployeeRecommendation) => action.payload),
      switchMap((recommendation: RecommendationInterface) => {
        return this.recommendationRestConnector.deleteEmployeeRecommendation(recommendation).pipe(
          map((recommendation: RecommendationInterface) => new DeleteEmployeeRecommendationCompleted(recommendation.id)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  /*
  Create/Update/Delete Recommendation Template Category
  */
  @Effect()
  public createRecommendationCategory$: Observable<Action> = this.actions$
  .pipe(
    ofType(RecommendationActionType.CreateRecommendationCategory),
    map((action: CreateRecommendationCategory) => action.payload),
    switchMap((categoryData: RecommendationCategoryInterface) => {
      return this.recommendationRestConnector.createRecommendationCategory(categoryData).pipe(
        /*    map((updatedCategoryData: RecommendationCategoryInterface) => new CreateRecommendationCategoryCompleted(updatedCategoryData)), */
           switchMap((updatedCategoryData: RecommendationCategoryInterface) => {
             return [
               new CreateRecommendationCategoryCompleted(updatedCategoryData),
               new LoadRecommendationsLibrary(),
             ];
           }),
           catchError((err: any) => of(createErrorRaised(new Error({
             message: ErrorMessage.unexpected,
             errorResponse: err,
             source: this.source,
           })))),
      );
    }),
  );

  @Effect()
  public updateRecommendationCategory$: Observable<Action> = this.actions$
  .pipe(
    ofType(RecommendationActionType.UpdateRecommendationCategory),
    map((action: UpdateRecommendationCategory) => action.payload),
    switchMap((categoryData: RecommendationCategoryInterface) => {
      return this.recommendationRestConnector.updateRecommendationCategory(categoryData).pipe(
        map((updatedCategoryData: RecommendationCategoryInterface) => new UpdateRecommendationCategoryCompleted(updatedCategoryData)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public deleteRecommendationCategory$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.DeleteRecommendationCategory),
      map((action: DeleteRecommendationCategory) => action.payload),
      switchMap((categoryToDelete: RecommendationCategoryInterface) => {
        return this.recommendationRestConnector.deleteRecommendationCategory(categoryToDelete).pipe(
          map((deletedCategory: RecommendationCategoryInterface) => new DeleteRecommendationCategoryCompleted(deletedCategory)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  /**
   * Create/Update/Delete Recommendation Template
   */
  @Effect()
  public createRecommendationTemplate$: Observable<Action> = this.actions$
  .pipe(
    ofType(RecommendationActionType.CreateRecommendationTemplate),
    map((action: CreateRecommendationTemplate) => action.payload),
    switchMap((templateData: RecommendationTemplateInterface) => {
      return this.recommendationRestConnector.createRecommendationTemplate(templateData).pipe(
        switchMap((createdTemplateData: RecommendationTemplateInterface) => { return [new CreateRecommendationTemplateCompleted(createdTemplateData),
          new LoadRecommendationsLibrary(),
        ];
        }),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public updateRecommendationTemplate$: Observable<Action> = this.actions$
  .pipe(
    ofType(RecommendationActionType.UpdateRecommendationTemplate),
    map((action: UpdateRecommendationTemplate) => action.payload),
    switchMap((templateToUpdate: RecommendationTemplateInterface) => {
      return this.recommendationRestConnector.updateRecommendationTemplate(templateToUpdate).pipe(
        switchMap((updatedTemplate: RecommendationTemplateInterface) => {
          return [new UpdateRecommendationTemplateCompleted(updatedTemplate),
            new LoadRecommendationsLibrary(),
          ]; }),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public deleteRecommendationTemplate$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.DeleteRecommendationTemplate),
      map((action: DeleteRecommendationTemplate) => action.payload),
      switchMap((templateToDelete: RecommendationTemplateInterface) => {
        return this.recommendationRestConnector.deleteRecommendationTemplate(templateToDelete).pipe(
          map((deletedTemplate: RecommendationTemplateInterface) => new DeleteRecommendationTemplateCompleted(deletedTemplate)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  /**
   * Create/Update/Delete Recommendation Template Rule
   */
  @Effect()
  public createRecommendationTemplateRule$: Observable<Action> = this.actions$
  .pipe(
    ofType(RecommendationActionType.CreateRecommendationTemplateRule),
    map((action: CreateRecommendationTemplateRule) => action.payload),
    switchMap((payload: {recommendation: RecommendationInterface, rule: RecommendationTemplateRuleInterface}) => {
      return this.recommendationRestConnector.createRecommendationTemplateRule(payload.recommendation, payload.rule).pipe(
        map((updatedTemplateData: RecommendationTemplateInterface) => new CreateRecommendationTemplateRuleCompleted(updatedTemplateData)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public updateRecommendationTemplateRule$: Observable<Action> = this.actions$
  .pipe(
    ofType(RecommendationActionType.UpdateRecommendationTemplateRule),
    map((action: UpdateRecommendationTemplateRule) => action.payload),
    switchMap((payload: {recommendation: RecommendationInterface, rule: RecommendationTemplateRuleInterface}) => {
      return this.recommendationRestConnector.updateRecommendationTemplateRule(payload.recommendation, payload.rule).pipe(
        map((updatedTemplate: RecommendationTemplateInterface) => new UpdateRecommendationTemplateRuleCompleted(updatedTemplate)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public deleteRecommendationTemplateRule$: Observable<Action> = this.actions$
    .pipe(
      ofType(RecommendationActionType.DeleteRecommendationTemplateRule),
      map((action: DeleteRecommendationTemplateRule) => action.payload),
      switchMap((payload: {recommendation: RecommendationInterface, rule: RecommendationTemplateRuleInterface}) => {
        return this.recommendationRestConnector.deleteRecommendationTemplateRule(payload.recommendation, payload.rule).pipe(
          map((updatedTemplate: RecommendationTemplateInterface) => new DeleteRecommendationTemplateRuleCompleted(updatedTemplate)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  constructor(private readonly actions$: Actions, private readonly recommendationRestConnector: RecommendationRestConnectorService) {}

}
